import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage({ onAuthSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [useClubCode, setUseClubCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    clubName: '',
    phone: '',
    clubCode: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate terms acceptance for signup
    if (!isLogin && !acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      setLoading(false);
      return;
    }

    try {
      let endpoint, payload;
      
      if (isLogin) {
        endpoint = '/auth/login';
        payload = { email: formData.email, password: formData.password };
      } else if (useClubCode) {
        endpoint = '/auth/signup-with-code';
        payload = { 
          email: formData.email, 
          password: formData.password,
          clubCode: formData.clubCode 
        };
      } else {
        endpoint = '/auth/signup';
        payload = {
          email: formData.email,
          password: formData.password,
          clubName: formData.clubName,
          phone: formData.phone
        };
      }

      const response = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem('token', response.data.access_token);
      toast.success(isLogin ? 'Logged in successfully' : 'Account created successfully');
      onAuthSuccess();
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <img src="/scorz-logo.png" alt="Scorz Logo" className="h-24 md:h-32 w-auto" />
          </div>
        </div>

        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-xl md:text-2xl" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {isLogin ? 'Login' : 'Sign Up'}
            </CardTitle>
            <CardDescription className="text-sm">
              {isLogin 
                ? 'Enter your credentials to access your club account' 
                : useClubCode
                  ? 'Join your club using a club code'
                  : 'Create a new club account'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && !useClubCode && (
                <>
                  <div>
                    <Label htmlFor="clubName">Club Name</Label>
                    <Input
                      id="clubName"
                      name="clubName"
                      data-testid="signup-club-name-input"
                      type="text"
                      required
                      value={formData.clubName}
                      onChange={handleChange}
                      placeholder="Enter your club name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="signup-phone-input"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter club phone number"
                    />
                  </div>
                </>
              )}

              {!isLogin && useClubCode && (
                <div>
                  <Label htmlFor="clubCode">Club Code</Label>
                  <Input
                    id="clubCode"
                    name="clubCode"
                    data-testid="signup-club-code-input"
                    type="text"
                    required
                    value={formData.clubCode}
                    onChange={handleChange}
                    placeholder="Enter your club code"
                    maxLength={8}
                  />
                  <p className="text-xs text-gray-500 mt-1">Ask your club admin for the club code</p>
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  data-testid={isLogin ? 'login-email-input' : 'signup-email-input'}
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <a
                      href="/forgot-password"
                      className="text-sm text-[#000000] hover:underline"
                    >
                      Forgot Password?
                    </a>
                  )}
                </div>
                <Input
                  id="password"
                  name="password"
                  data-testid={isLogin ? 'login-password-input' : 'signup-password-input'}
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                />
              </div>

              {!isLogin && (
                <div className="flex items-start space-x-2 p-4 bg-gray-50 rounded-lg">
                  <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={setAcceptedTerms}
                  />
                  <Label htmlFor="terms" className="text-xs leading-relaxed cursor-pointer">
                    I understand that when I create Gymnast, Coach, or Judge profiles, the information is saved within my account and only Scorz Super Admins can view it. It is my responsibility as the club to remove gymnasts, coaches, or judges no longer at the club, and to update gymnast floor music. I have full control over my account at any time.
                  </Label>
                </div>
              )}

              <Button
                type="submit"
                data-testid={isLogin ? 'login-submit-button' : 'signup-submit-button'}
                disabled={loading}
                className="w-full bg-[#000000] hover:bg-[#333333] text-white"
              >
                {loading ? 'Please wait...' : isLogin ? 'Login' : 'Sign Up'}
              </Button>
            </form>

            <div className="mt-6 space-y-3">
              {!isLogin && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setUseClubCode(!useClubCode)}
                    className="text-[#000000] hover:underline font-medium text-sm"
                  >
                    {useClubCode ? 'Register a new club instead' : 'USE CLUB CODE'}
                  </button>
                </div>
              )}
              
              <div className="text-center">
                <button
                  type="button"
                  data-testid="toggle-auth-mode-button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setUseClubCode(false);
                  }}
                  className="text-[#000000] hover:underline font-medium"
                >
                  {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Login'}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}